import React, { useState, useEffect, useCallback } from "react";
import {
  createBlog,
  getBlogs,
  deleteBlog,
  updateBlog,
  reorderBlogs,
} from "../api/blogAPI";
import {
  getProjects,
  deleteProject,
  updateProject,
  createProject,
  reorderProjects,
} from "../api/projectAPI";

import { useAuthContext } from "../context/authContext";
import DraggableList from "../components/DraggableList";
import BlogForm from "../components/BlogForm";
import ProjectForm from "../components/ProjectForm";
import BulkActionsBar from "../components/BulkActionsBar";
import { useToast } from "../components/Toast";
import { FiSearch, FiRefreshCw, FiCheckSquare } from "react-icons/fi";

const AdminDashboard = () => {
  const { auth } = useAuthContext();
  const toast = useToast();
  const [blogs, setBlogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState({
    blogs: false,
    projects: false,
    action: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all, published, unpublished

  const [activeTab, setActiveTab] = useState("blogs"); // blogs, projects

  // Bulk selection state
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);

  const fetchBlogs = useCallback(async () => {
    if (!auth.token) {
      if (process.env.NODE_ENV === "development") {
        console.log("Skipping blog fetch - no auth token available");
      }
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, blogs: true }));
      const blogsData = await getBlogs(auth.token);
      setBlogs(Array.isArray(blogsData) ? blogsData : []);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Failed to fetch blogs. Please try again.");
      setBlogs([]);
    } finally {
      setLoading((prev) => ({ ...prev, blogs: false }));
    }
  }, [auth.token, toast]);

  const fetchProjects = useCallback(async () => {
    if (!auth.token) {
      if (process.env.NODE_ENV === "development") {
        console.log("Skipping project fetch - no auth token available");
      }
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, projects: true }));
      const projectsData = await getProjects(auth.token);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to fetch projects. Please try again.");
      setProjects([]);
    } finally {
      setLoading((prev) => ({ ...prev, projects: false }));
    }
  }, [auth.token, toast]);

  // Fetch data when component mounts
  useEffect(() => {
    if (auth.isAuthenticated && auth.token && auth.userRole) {
      fetchBlogs();
      fetchProjects();

      if (process.env.NODE_ENV === "development") {
        console.log("AdminDashboard: Fetching data on mount");
      }
    }
  }, [
    auth.isAuthenticated,
    auth.token,
    auth.userRole,
    fetchBlogs,
    fetchProjects,
  ]);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedBlogs([]);
    setSelectedProjects([]);
  }, [activeTab]);

  // Blog handlers
  const handleCreateBlog = async (blogData) => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      await createBlog(blogData, auth.token);
      await fetchBlogs();
      setSelectedBlog(null);
      toast.success(`Blog "${blogData.title}" created successfully!`);
    } catch (error) {
      console.error("Error creating blog:", error);
      toast.error("Failed to create blog. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleUpdateBlog = async (blogData) => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      await updateBlog(selectedBlog._id, blogData, auth.token);
      await fetchBlogs();
      setSelectedBlog(null);
      toast.success(`Blog "${blogData.title}" updated successfully!`);
    } catch (error) {
      console.error("Error updating blog:", error);
      toast.error("Failed to update blog. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleDeleteBlog = async (blogId) => {
    const blog = blogs.find((b) => b._id === blogId);
    if (window.confirm(`Are you sure you want to delete "${blog?.title}"?`)) {
      try {
        await deleteBlog(blogId, auth.token);
        await fetchBlogs();
        toast.success("Blog deleted successfully!");
      } catch (error) {
        console.error("Error deleting blog:", error);
        toast.error("Failed to delete blog. Please try again.");
      }
    }
  };

  const handleReorderBlogs = async (blogOrders, newBlogs) => {
    try {
      // Optimistically update UI
      setBlogs(newBlogs);
      await reorderBlogs(blogOrders, auth.token);
      toast.success("Blogs reordered successfully!");
    } catch (error) {
      console.error("Error reordering blogs:", error);
      toast.error("Failed to reorder blogs. Please try again.");
      // Revert on error
      fetchBlogs();
    }
  };

  // Project handlers
  const handleCreateProject = async (projectData) => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      await createProject(projectData, auth.token);
      await fetchProjects();
      setSelectedProject(null);
      toast.success(`Project "${projectData.title}" created successfully!`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleUpdateProject = async (projectData) => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      await updateProject(selectedProject._id, projectData, auth.token);
      await fetchProjects();
      setSelectedProject(null);
      toast.success(`Project "${projectData.title}" updated successfully!`);
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error("Failed to update project. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleDeleteProject = async (projectId) => {
    const project = projects.find((p) => p._id === projectId);
    if (
      window.confirm(`Are you sure you want to delete "${project?.title}"?`)
    ) {
      try {
        await deleteProject(projectId, auth.token);
        await fetchProjects();
        toast.success("Project deleted successfully!");
      } catch (error) {
        console.error("Error deleting project:", error);
        toast.error("Failed to delete project. Please try again.");
      }
    }
  };

  const handleReorderProjects = async (projectOrders, newProjects) => {
    try {
      // Optimistically update UI
      setProjects(newProjects);
      await reorderProjects(projectOrders, auth.token);
      toast.success("Projects reordered successfully!");
    } catch (error) {
      console.error("Error reordering projects:", error);
      toast.error("Failed to reorder projects. Please try again.");
      // Revert on error
      fetchProjects();
    }
  };

  // Bulk operation handlers
  const handleBlogSelect = (blogId, checked) => {
    if (checked) {
      setSelectedBlogs((prev) => [...prev, blogId]);
    } else {
      setSelectedBlogs((prev) => prev.filter((id) => id !== blogId));
    }
  };

  const handleProjectSelect = (projectId, checked) => {
    if (checked) {
      setSelectedProjects((prev) => [...prev, projectId]);
    } else {
      setSelectedProjects((prev) => prev.filter((id) => id !== projectId));
    }
  };

  const handleSelectAllBlogs = () => {
    setSelectedBlogs(filteredBlogs.map((blog) => blog._id));
  };

  const handleSelectAllProjects = () => {
    setSelectedProjects(filteredProjects.map((project) => project._id));
  };

  const handleDeselectAllBlogs = () => {
    setSelectedBlogs([]);
  };

  const handleDeselectAllProjects = () => {
    setSelectedProjects([]);
  };

  const handleBulkDeleteBlogs = async () => {
    if (selectedBlogs.length === 0) return;

    const selectedTitles = blogs
      .filter((blog) => selectedBlogs.includes(blog._id))
      .map((blog) => blog.title)
      .join(", ");

    if (
      window.confirm(
        `Are you sure you want to delete these ${selectedBlogs.length} blogs?\n\n${selectedTitles}`
      )
    ) {
      try {
        await Promise.all(
          selectedBlogs.map((blogId) => deleteBlog(blogId, auth.token))
        );
        await fetchBlogs();
        setSelectedBlogs([]);
        toast.success(`${selectedBlogs.length} blogs deleted successfully!`);
      } catch (error) {
        console.error("Error deleting blogs:", error);
        toast.error("Failed to delete some blogs. Please try again.");
      }
    }
  };

  const handleBulkDeleteProjects = async () => {
    if (selectedProjects.length === 0) return;

    const selectedTitles = projects
      .filter((project) => selectedProjects.includes(project._id))
      .map((project) => project.title)
      .join(", ");

    if (
      window.confirm(
        `Are you sure you want to delete these ${selectedProjects.length} projects?\n\n${selectedTitles}`
      )
    ) {
      try {
        await Promise.all(
          selectedProjects.map((projectId) =>
            deleteProject(projectId, auth.token)
          )
        );
        await fetchProjects();
        setSelectedProjects([]);
        toast.success(
          `${selectedProjects.length} projects deleted successfully!`
        );
      } catch (error) {
        console.error("Error deleting projects:", error);
        toast.error("Failed to delete some projects. Please try again.");
      }
    }
  };

  const handleBulkPublishBlogs = async () => {
    if (selectedBlogs.length === 0) return;

    try {
      await Promise.all(
        selectedBlogs.map((blogId) =>
          updateBlog(blogId, { published: true }, auth.token)
        )
      );
      await fetchBlogs();
      setSelectedBlogs([]);
      toast.success(`${selectedBlogs.length} blogs published successfully!`);
    } catch (error) {
      console.error("Error publishing blogs:", error);
      toast.error("Failed to publish some blogs. Please try again.");
    }
  };

  const handleBulkUnpublishBlogs = async () => {
    if (selectedBlogs.length === 0) return;

    try {
      await Promise.all(
        selectedBlogs.map((blogId) =>
          updateBlog(blogId, { published: false }, auth.token)
        )
      );
      await fetchBlogs();
      setSelectedBlogs([]);
      toast.success(`${selectedBlogs.length} blogs unpublished successfully!`);
    } catch (error) {
      console.error("Error unpublishing blogs:", error);
      toast.error("Failed to unpublish some blogs. Please try again.");
    }
  };

  const handleBulkPublishProjects = async () => {
    if (selectedProjects.length === 0) return;

    try {
      await Promise.all(
        selectedProjects.map((projectId) =>
          updateProject(projectId, { published: true }, auth.token)
        )
      );
      await fetchProjects();
      setSelectedProjects([]);
      toast.success(
        `${selectedProjects.length} projects published successfully!`
      );
    } catch (error) {
      console.error("Error publishing projects:", error);
      toast.error("Failed to publish some projects. Please try again.");
    }
  };

  const handleBulkUnpublishProjects = async () => {
    if (selectedProjects.length === 0) return;

    try {
      await Promise.all(
        selectedProjects.map((projectId) =>
          updateProject(projectId, { published: false }, auth.token)
        )
      );
      await fetchProjects();
      setSelectedProjects([]);
      toast.success(
        `${selectedProjects.length} projects unpublished successfully!`
      );
    } catch (error) {
      console.error("Error unpublishing projects:", error);
      toast.error("Failed to unpublish some projects. Please try again.");
    }
  };

  // Filter and search functions
  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.content.toLowerCase().includes(searchTerm.toLowerCase());

    if (filter === "published")
      return matchesSearch && blog.published !== false;
    if (filter === "unpublished")
      return matchesSearch && blog.published === false;
    return matchesSearch;
  });

  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase());

    if (filter === "published")
      return matchesSearch && project.published !== false;
    if (filter === "unpublished")
      return matchesSearch && project.published === false;
    return matchesSearch;
  });

  const renderProjectContent = (project) => (
    <>
      <div className="flex items-start gap-3 mb-3">
        {project.thumbnail && (
          <img
            src={project.thumbnail}
            alt={project.title}
            className="w-16 h-16 object-cover rounded-md flex-shrink-0"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        )}
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-brand-gold mb-1 line-clamp-1">
            {project.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 mb-2">
            {project.description}
          </p>
          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 text-xs underline"
            >
              View Project
            </a>
          )}
        </div>
      </div>
      {project.published === false && (
        <span className="inline-block bg-yellow-600/20 text-yellow-400 text-xs px-2 py-1 rounded">
          Unpublished
        </span>
      )}
    </>
  );

  const renderBlogContent = (blog) => (
    <>
      <h3 className="text-xl font-semibold text-brand-gold mb-2 line-clamp-2">
        {blog.title}
      </h3>
      <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-2">{blog.content}</p>
      {blog.published === false && (
        <span className="inline-block bg-yellow-600/20 text-yellow-400 text-xs px-2 py-1 rounded">
          Unpublished
        </span>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-brand-darker text-gray-800 dark:text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Show loading if auth is not checked or incomplete */}
        {!auth.userRole || !auth.token ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-gold mb-4"></div>
            <p className="text-gray-700 dark:text-brand-cream text-lg">Loading Admin Dashboard...</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
                <span className="text-gradient">
                  Admin Dashboard
                </span>
              </h1>
              <p className="text-gray-400 text-center">
                Manage your portfolio content with drag-and-drop reordering
              </p>
            </div>

            {/* Tabs */}
            <div className="flex justify-center mb-8">
              <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab("blogs")}
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    activeTab === "blogs"
                      ? "bg-brand-gold text-black font-bold"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  }`}
                >
                  Blogs ({blogs.length})
                </button>
                <button
                  onClick={() => setActiveTab("projects")}
                  className={`px-6 py-2 rounded-md font-medium transition-colors ${
                    activeTab === "projects"
                      ? "bg-brand-gold text-black font-bold"
                      : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  }`}
                >
                  Projects ({projects.length})
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  />
                </div>

                {/* Filter and Controls */}
                <div className="flex gap-2">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-gold"
                  >
                    <option value="all">All Items</option>
                    <option value="published">Published</option>
                    <option value="unpublished">Unpublished</option>
                  </select>

                  <button
                    onClick={() => setBulkMode(!bulkMode)}
                    className={`px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                      bulkMode
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300"
                    }`}
                    title="Toggle bulk selection mode"
                  >
                    <FiCheckSquare size={16} />
                    Bulk
                  </button>

                  <button
                    onClick={activeTab === "blogs" ? fetchBlogs : fetchProjects}
                    className="p-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
                    title="Refresh"
                    disabled={loading.blogs || loading.projects}
                  >
                    <FiRefreshCw
                      size={16}
                      className={
                        loading.blogs || loading.projects ? "animate-spin" : ""
                      }
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {activeTab === "blogs" && (
              <BulkActionsBar
                selectedItems={selectedBlogs}
                totalItems={filteredBlogs.length}
                onSelectAll={handleSelectAllBlogs}
                onDeselectAll={handleDeselectAllBlogs}
                onBulkDelete={handleBulkDeleteBlogs}
                onBulkPublish={handleBulkPublishBlogs}
                onBulkUnpublish={handleBulkUnpublishBlogs}
                itemType="blogs"
              />
            )}

            {activeTab === "projects" && (
              <BulkActionsBar
                selectedItems={selectedProjects}
                totalItems={filteredProjects.length}
                onSelectAll={handleSelectAllProjects}
                onDeselectAll={handleDeselectAllProjects}
                onBulkDelete={handleBulkDeleteProjects}
                onBulkPublish={handleBulkPublishProjects}
                onBulkUnpublish={handleBulkUnpublishProjects}
                itemType="projects"
              />
            )}

            {/* Form Section */}
            <div className="mb-8">
              {activeTab === "blogs" ? (
                <BlogForm
                  selectedBlog={selectedBlog}
                  onSubmit={selectedBlog ? handleUpdateBlog : handleCreateBlog}
                  onCancel={() => setSelectedBlog(null)}
                  isLoading={loading.action}
                />
              ) : (
                <ProjectForm
                  selectedProject={selectedProject}
                  onSubmit={
                    selectedProject ? handleUpdateProject : handleCreateProject
                  }
                  onCancel={() => setSelectedProject(null)}
                  isLoading={loading.action}
                />
              )}
            </div>

            {/* Content Lists */}
            <div className="space-y-6">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-brand-gold">
                    {activeTab === "blogs" ? "All Blogs" : "All Projects"}
                    <span className="text-gray-400 text-lg ml-2">
                      (
                      {activeTab === "blogs"
                        ? filteredBlogs.length
                        : filteredProjects.length}
                      )
                    </span>
                  </h2>
                  <div className="text-sm text-gray-400">
                    💡 Drag items to reorder them
                  </div>
                </div>

                {activeTab === "blogs" ? (
                  loading.blogs ? (
                    <div className="text-center py-8 text-gray-400">
                      Loading blogs...
                    </div>
                  ) : filteredBlogs.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      {searchTerm
                        ? "No blogs match your search."
                        : "No blogs yet. Create your first blog above!"}
                    </div>
                  ) : (
                    <DraggableList
                      items={filteredBlogs}
                      onReorder={handleReorderBlogs}
                      onEdit={setSelectedBlog}
                      onDelete={handleDeleteBlog}
                      onView={(blog) =>
                        window.open(`/blogs/${blog._id}`, "_blank")
                      }
                      itemType="blog"
                      renderContent={renderBlogContent}
                      selectedItems={selectedBlogs}
                      onItemSelect={handleBlogSelect}
                      bulkMode={bulkMode}
                    />
                  )
                ) : loading.projects ? (
                  <div className="text-center py-8 text-gray-400">
                    Loading projects...
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    {searchTerm
                      ? "No projects match your search."
                      : "No projects yet. Create your first project above!"}
                  </div>
                ) : (
                  <DraggableList
                    items={filteredProjects}
                    onReorder={handleReorderProjects}
                    onEdit={setSelectedProject}
                    onDelete={handleDeleteProject}
                    onView={(project) => window.open(project.link, "_blank")}
                    itemType="project"
                    renderContent={renderProjectContent}
                    selectedItems={selectedProjects}
                    onItemSelect={handleProjectSelect}
                    bulkMode={bulkMode}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
